import React, { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { addDays } from 'date-fns';
import {
  Container,
  FormControlLabel,
  Grid,
  makeStyles,
  Radio,
  RadioGroup,
  Theme,
  Typography
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import DatePicker from 'components/@common/Inputs/DatePicker';
import { ActivationRangeOptions } from 'types/enums';

export type Props = {
  nowDate: Date;
  minDate?: object | string | number | Date | null;
  maxDate?: object | string | number | Date | null;
  isValidDate: boolean;
  activationRangeValue: string;
  activationRangeDate: Date | null;
  warningText?: React.ReactNode;
  handleActivationChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleDateActivationChange: (date: Date | null) => void;
};

const ActivateMastercodeForm = ({
  minDate,
  maxDate,
  nowDate,
  isValidDate,
  activationRangeValue,
  activationRangeDate,
  warningText,
  handleActivationChange,
  handleDateActivationChange
}: Props) => {
  const classes = useStyles();
  const { t } = useTranslation('Order');

  return (
    <Container maxWidth="sm">
      <Grid item xs={12} sm={12}>
        <div className={classes.radioContainer}>
          <Typography variant="subtitle1" className={classes.radioText}>
            {t('activationDate')}
          </Typography>
          <RadioGroup
            defaultValue={ActivationRangeOptions.NOW}
            value={activationRangeValue}
            aria-label={t('activationDate')}
            name="assignDateActivation"
            className={classes.radioGroup}
            onChange={handleActivationChange}
          >
            <FormControlLabel
              value={ActivationRangeOptions.NOW}
              control={<Radio />}
              label={t('activationDateNow')}
              classes={{ label: classes.radioFormControlLabel }}
            />
            <FormControlLabel
              value={ActivationRangeOptions.LATER}
              control={<Radio />}
              label={t('activationDateLater')}
              classes={{ label: classes.radioFormControlLabel }}
            />
          </RadioGroup>
        </div>
        {activationRangeValue === ActivationRangeOptions.LATER && (
          <Fragment>
            <DatePicker
              id="activation-date-picker"
              defaultValue={addDays(nowDate, 1)}
              className={classes.inputField}
              minDate={minDate}
              maxDate={maxDate}
              KeyboardButtonProps={{ 'aria-label': t('activationDate') }}
              inputProps={{ 'aria-label': t('activationDate') }}
              label={t('activationDatePickerHelp')}
              onChange={handleDateActivationChange}
              value={activationRangeDate}
            />
            {warningText && activationRangeDate && isValidDate && (
              <Alert severity="info">{warningText}</Alert>
            )}
          </Fragment>
        )}
      </Grid>
    </Container>
  );
};

export default ActivateMastercodeForm;

const useStyles = makeStyles((theme: Theme) => ({
  inputField: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2)
  },
  radioText: {
    fontWeight: 500
  },
  radioContainer: {
    marginTop: theme.spacing(2.5)
  },
  radioGroup: {
    display: 'flex',
    flexDirection: 'row'
  },
  radioFormControlLabel: {
    ...theme.typography.subtitle1,
    fontSize: '1rem',
    marginLeft: 0
  }
}));
